/**
 * Silva City — 66-acre site layout data (derived from the master plan PDF).
 *
 * COORDINATE SYSTEM
 * -----------------
 * All values are normalized inside a 0..100 grid where the TOP-LEFT of the PDF
 * is (0, 0) and the BOTTOM-RIGHT is (100, 100). North is at y = 0.
 *
 *  pdfToWorld(pdfX, pdfY) -> [worldX, worldZ]
 *    worldX =  (pdfX - 50) * SCALE     // east is +X
 *    worldZ =  (pdfY - 50) * SCALE     // south is +Z (so north is -Z)
 *
 * SCALE: half-extent multiplier — 100 normalized units → ~50 world units.
 */

export const SCALE = 0.5;

/** Map a PDF normalized point (x, y) → world space [x, z]. */
export const pdfToWorld = ([x, y]) => [(x - 50) * SCALE, (y - 50) * SCALE];

/** Irregular polygon describing the 66-acre site boundary.
 *  Clockwise, starting from the northernmost point. ~27 vertices. */
export const BOUNDARY_PDF = [
  [50, 1],
  [48, 4],
  [50, 10],
  [40, 14],
  [32, 20],
  [27, 29],
  [20, 36],
  [14, 44],
  [10, 52],
  [14, 60],
  [12, 68],
  [10, 75],
  [15, 85],
  [25, 92],
  [40, 95],
  [55, 95],
  [70, 93],
  [82, 88],
  [90, 80],
  [95, 70],
  [98, 58],
  [97, 46],
  [92, 32],
  [85, 22],
  [78, 14],
  [68, 9],
  [58, 6],
];

/** Mountain ridge polylines hugging the north & west edges (PDF coords). */
export const MOUNTAIN_RIDGES_PDF = [
  // North ridge (curves with the boundary, NW → NE)
  [
    [40, -4],
    [46, -3],
    [52, -3],
    [60, -2],
    [68, -1],
    [76, 2],
    [84, 7],
    [90, 14],
    [95, 22],
  ],
  // West ridge (W → SW)
  [
    [4, 26],
    [2, 34],
    [1, 42],
    [2, 50],
    [3, 58],
    [4, 66],
    [5, 74],
    [9, 82],
    [16, 90],
  ],
];

/**
 * Roads — list of polylines (PDF coords).
 *
 *   `id` and `name`: useful for debug/UI
 *   `width`: PDF-scale width (will be multiplied by SCALE)
 *   `dashed`: true → center line will be dashed white (asphalt road),
 *             false → solid gold (premium spine)
 */
export const ROADS_PDF = [
  // 0 — Main spine: enters from east (entrance gate) → curves to clubhouse → continues NW
  {
    id: "spine",
    width: 1.8,
    accent: "gold",
    points: [
      [99, 56],
      [92, 56],
      [85, 56],
      [78, 55],
      [70, 55],
      [60, 55],
      [50, 55],
      [40, 56],
      [32, 60],
      [24, 65],
    ],
  },
  // 1 — North vertical artery to playground / temple
  {
    id: "north_artery",
    width: 1.3,
    accent: "dashed",
    points: [
      [50, 55],
      [50, 48],
      [50, 40],
      [50, 32],
      [50, 24],
    ],
  },
  // 2 — Branch to temple (NE)
  {
    id: "to_temple",
    width: 1.1,
    accent: "dashed",
    points: [
      [50, 32],
      [60, 30],
      [70, 28],
      [78, 26],
    ],
  },
  // 3 — South artery to amphitheater
  {
    id: "south_artery",
    width: 1.3,
    accent: "dashed",
    points: [
      [60, 55],
      [62, 64],
      [66, 72],
      [70, 80],
    ],
  },
  // 4 — East loop (around east edge — past Park 1)
  {
    id: "east_loop",
    width: 0.9,
    accent: "dashed",
    points: [
      [78, 26],
      [85, 35],
      [86, 45],
      [82, 52],
      [78, 55],
    ],
  },
  // 5 — NW connector (toward Wellness Centre)
  {
    id: "nw_connector",
    width: 1.0,
    accent: "dashed",
    points: [
      [40, 56],
      [32, 50],
      [25, 46],
      [20, 40],
    ],
  },
  // 6 — South loop (around south plots toward amphitheater)
  {
    id: "south_loop",
    width: 0.9,
    accent: "dashed",
    points: [
      [40, 56],
      [36, 64],
      [38, 72],
      [42, 80],
      [50, 84],
      [58, 82],
      [62, 78],
    ],
  },
  // 7 — Tertiary EW (north-west grid)
  {
    id: "tertiary_ew",
    width: 0.75,
    accent: "dashed",
    points: [
      [24, 40],
      [34, 40],
      [44, 40],
      [52, 40],
    ],
  },
  // 8 — Tertiary NE (between temple & social centre)
  {
    id: "tertiary_ne",
    width: 0.7,
    accent: "dashed",
    points: [
      [60, 30],
      [60, 38],
      [60, 46],
    ],
  },
];

/**
 * Residential plot clusters — rectangular blocks roughly matching the master
 * plan (each cluster will be filled with a subtle plot-grid pattern of small
 * houses on green lawn).
 *
 * `corners` = [pdfX_topLeft, pdfY_topLeft, pdfX_bottomRight, pdfY_bottomRight]
 */
export const PLOT_CLUSTERS_PDF = [
  // NW cluster (left of playground)
  { id: "nw", corners: [22, 18, 44, 36], cols: 7, rows: 5 },
  // NE cluster (right of temple)
  { id: "ne", corners: [56, 12, 75, 26], cols: 6, rows: 4 },
  // West cluster (below Wellness Centre)
  { id: "w_mid", corners: [16, 46, 34, 64], cols: 6, rows: 5 },
  // Central-south cluster (between clubhouse and amphitheater)
  { id: "central_s", corners: [40, 60, 58, 76], cols: 6, rows: 5 },
  // South-east cluster (left of amphitheater)
  { id: "se", corners: [62, 58, 80, 74], cols: 6, rows: 5 },
  // South cluster (below south arteries)
  { id: "south", corners: [30, 80, 60, 90], cols: 9, rows: 3 },
  // East cluster (south of temple)
  { id: "east", corners: [80, 42, 92, 58], cols: 4, rows: 5 },
];

/**
 * HOTSPOTS — interactive amenities, each rendered as a unique 3D building
 * with a floating gold marker that opens a glassmorphic side panel popup.
 *
 *   pos      - PDF normalized [x, y]
 *   building - which 3D component to render
 *   rot      - building Y rotation (radians) so it faces the road/spine
 *   audio    - audio key under /api/audio/{audio}
 */
export const HOTSPOTS = [
  {
    id: "entrance",
    title: "Strategic Gateway",
    eyebrow: "Hotspot 01 — Entrance",
    pdfPos: [96, 56],
    building: "entrance",
    rot: -Math.PI / 2, // facing west (into the township)
    color: "gold",
    description:
      "Secure gated entrance on Shandon Road. Eighteen-metre wide approach with 24×7 manned security, gatehouse, and visitor management.",
    badges: ["18m Approach Road", "24/7 Security", "Purandar Airport 15–20 min"],
    audio: "entrance",
    mediaKey: "hotspot_entrance_image",
  },
  {
    id: "clubhouse",
    title: "The Clubhouse",
    eyebrow: "Hotspot 02 — Clubhouse",
    pdfPos: [50, 55],
    building: "clubhouse",
    rot: Math.PI, // facing south
    color: "gold",
    description:
      "A private members' clubhouse at the heart of the township — pool, gym, banquet lawn and indoor games, opening onto Park 2.",
    badges: ["Pool", "Gym", "Banquet Lawn", "Indoor Games"],
    audio: "clubhouse",
    mediaKey: "hotspot_clubhouse_image",
  },
  {
    id: "temple",
    title: "The Temple",
    eyebrow: "Hotspot 03 — Temple",
    pdfPos: [78, 26],
    building: "temple",
    rot: Math.PI, // facing south (entry on the south side)
    color: "emerald",
    description:
      "A serene temple complex in the north-eastern precinct, framed by Park 1 — the spiritual anchor of Silva City.",
    badges: ["Park 1", "Daily Aarti", "Festival Plaza"],
    audio: "temple",
    mediaKey: "hotspot_temple_image",
  },
  {
    id: "playground",
    title: "Playground",
    eyebrow: "Hotspot 04 — Playground",
    pdfPos: [50, 22],
    building: "playground",
    rot: 0,
    color: "emerald",
    description:
      "Purpose-built children's playground in the northern sector with modern equipment, soft impact flooring, and adjacent Park 3.",
    badges: ["Park 3", "Soft Flooring", "Shaded Seating"],
    audio: "playground",
    mediaKey: "hotspot_playground_image",
  },
  {
    id: "amphitheater",
    title: "Open-Air Amphitheatre",
    eyebrow: "Hotspot 05 — Amphitheatre",
    pdfPos: [70, 82],
    building: "amphitheater",
    rot: Math.PI, // stage facing south, seating opens north toward residents
    color: "gold",
    description:
      "Open-air amphitheatre in the south-eastern quadrant — tiered stone seating for music, yoga, festivals and community gatherings.",
    badges: ["Open-air", "Community Stage", "Yoga & Cultural Events"],
    audio: "amphitheater",
    mediaKey: "hotspot_amphitheater_image",
  },
  {
    id: "wellness",
    title: "Luxury Wellness Centre",
    eyebrow: "Hotspot 06 — Wellness",
    pdfPos: [20, 40],
    building: "wellness",
    rot: Math.PI / 2, // facing east (into township)
    color: "emerald",
    description:
      "A boutique wellness destination on the western precinct — meditation studio, spa, yoga halls, outdoor lap pool and ayurvedic treatments.",
    badges: ["Spa", "Yoga", "Lap Pool", "Ayurveda"],
    audio: "social_infrastructure",
    mediaKey: "hotspot_social_1_image",
  },
  {
    id: "social",
    title: "Community Social Centre",
    eyebrow: "Hotspot 07 — Social Centre",
    pdfPos: [60, 38],
    building: "social",
    rot: Math.PI,
    color: "emerald",
    description:
      "Civic hub for the eastern precinct — café, retail, co-working, daycare and a shared library. Daily life inside the gate.",
    badges: ["Café", "Co-working", "Daycare", "Library"],
    audio: "social_infrastructure",
    mediaKey: "hotspot_social_2_image",
  },
  {
    id: "park",
    title: "Central Park",
    eyebrow: "Hotspot 08 — Park",
    pdfPos: [42, 70],
    building: "park",
    rot: 0,
    color: "emerald",
    description:
      "A landscaped central park with native tree groves, walking loops, sit-out pavilions and lawn — the green spine of the township.",
    badges: ["Tree Groves", "Walking Loop", "Pavilion"],
    audio: "park",
    mediaKey: "hotspot_parks_image",
  },
];

/** Overview audio for the "Take a Tour" landing track. */
export const OVERVIEW_AUDIO = "silva_city_overview";
